import * as React from "react";

import { browserDefaultTimezone } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { DateEmailSchedulerForm, DateEmailSchedulerProps } from "./types";
import { useStyles } from "./useStyles";

import { DashboardDateTimePicker } from "@/dashboard-design-system/DateTimePicker";
import { DashboardDialog } from "@/dashboard-design-system/Dialog/DashboardDialog";
import { isRequired } from "@/helpers/validators";
import { useTranslate } from "@/hooks/useTranslate";
import { useLocaleContext } from "@/src/contexts/LocaleContext";

export const DateEmailScheduler: React.FC<Omit<DateEmailSchedulerProps, "format">> = ({
  isOpen,
  onCancel,
  onClose,
  onSubmit,
  title,
  cancelLabel,
  scheduledFor,
  minDate,
  maxDate,
}) => {
  const { t } = useTranslate();
  const classes = useStyles();

  const form = useForm<DateEmailSchedulerForm>({
    mode: "onChange",
    defaultValues: { scheduledFor },
  });
  const { locale } = useLocaleContext();

  const handleSubmit = form.handleSubmit(async (data) => {
    await onSubmit(data.scheduledFor);
    onClose?.();
  });

  return (
    <DashboardDialog
      title={title}
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      classes={{ paper: classes.paper }}
      actions={{
        main: {
          onClick: handleSubmit,
          children: t("common", "confirm"),
          "data-test": "date-email-scheduler-submit",
        },
        cancel: { children: cancelLabel, onClick: onCancel },
      }}
      hideIcon
    >
      <DashboardDateTimePicker
        name="scheduledFor"
        label={t("dashboard", "DateEmailScheduler.form.scheduledFor.label")}
        control={form.control}
        locale={locale}
        rules={[isRequired()]}
        errorMessage={form.formState.errors.scheduledFor && form.formState.errors.scheduledFor.message}
        timezone={browserDefaultTimezone}
        minDate={minDate || new Date()}
        maxDate={maxDate}
        data-test="date-email-scheduler"
        disablePast={true}
      />
    </DashboardDialog>
  );
};
